import { useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import logger from '../../utils/logger';
import { COLORS } from '../../styles/colors';

const VIEW_ORDER = ['front-left', 'side', 'rear-left', 'front', 'low-angle-front-quarter'];

const BREAKPOINT_WIDTH = 768;

const ImageCarousel = ({ images, placeholderImageUrl, imageFidelity }) => {
  const carouselRef = useRef(null);
  // Initialize with screen dimensions to ensure carousel renders immediately
  // This prevents the race condition where onLayout doesn't fire after modal dismissal
  const initialDimensions = Dimensions.get('window');
  const [containerSize, setContainerSize] = useState({
    width: initialDimensions.width > BREAKPOINT_WIDTH ? 600 : initialDimensions.width - 32,
    height: initialDimensions.width > BREAKPOINT_WIDTH ? 400 : 300,
  });
  const [screenWidth, setScreenWidth] = useState(initialDimensions.width);

  // Track screen width for responsive layout
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const isMobile = screenWidth < BREAKPOINT_WIDTH;

  const sortedImages = useMemo(() => {
    if (!images || images.length === 0) {
      return [];
    }

    const imageMap = new Map(images.map((img) => [img.angle_view, img]));
    const orderedImages = [];

    // Add images in the desired order
    VIEW_ORDER.forEach((view) => {
      if (imageMap.has(view)) {
        orderedImages.push(imageMap.get(view));
        imageMap.delete(view);
      }
    });

    // Add any remaining images
    const remainingImages = [...imageMap.values()];

    return [...orderedImages, ...remainingImages];
  }, [images]);

  const defaultImageIndex = useMemo(() => {
    const idx = sortedImages.findIndex((img) => img.is_default_display);
    return idx !== -1 ? idx : 0;
  }, [sortedImages]);

  const handlePrevious = () => {
    carouselRef.current?.prev();
  };

  const handleNext = () => {
    carouselRef.current?.next();
  };

  const onLayout = (event) => {
    const { width, height } = event.nativeEvent.layout;
    setContainerSize({ width, height });
  };

  const renderItem = ({ item }) => (
    <Image
      source={{ uri: item.url }}
      style={styles.image}
      resizeMode="contain"
      testID="carousel-image"
    />
  );

  // Always render the same container structure to preserve state across transitions
  return (
    <View style={styles.container} onLayout={onLayout}>
      {/* 3-tier fallback rendering: real images → placeholder image → generic "No Image Available" */}
      {!sortedImages || sortedImages.length === 0 ? (
        // No images - show placeholder or generic fallback
        placeholderImageUrl ? (
          <Image
            source={{ uri: placeholderImageUrl }}
            style={styles.placeholderImage}
            resizeMode="contain"
            testID="placeholder-image"
          />
        ) : (
          <View style={styles.imagePlaceholder}>
            <Icon name="camera-off" size={50} color="#cccccc" />
            <Text style={styles.noImageText}>Images Coming Soon</Text>
          </View>
        )
      ) : (
        // Has images - show carousel when container is sized
        <>
          {containerSize.width > 0 && (
            /* Gesture configuration for nested scrolling (v4 API):
               Uses onConfigurePanGesture to allow both horizontal carousel swipes
               and vertical page scrolling on mobile web. */
            <Carousel
              ref={carouselRef}
              loop
              width={containerSize.width}
              height={containerSize.height}
              autoPlay={false}
              data={sortedImages}
              defaultIndex={defaultImageIndex}
              scrollAnimationDuration={1000}
              renderItem={renderItem}
              onConfigurePanGesture={(gesture) => {
                // For web: allow vertical scrolling to pass through
                if (gesture.config) {
                  gesture.config.touchAction = 'pan-y';
                }
                gesture.activeOffsetX([-10, 10]);
                gesture.failOffsetY([-5, 5]);
              }}
            />
          )}
          {sortedImages.length > 1 && (
            <>
              <TouchableOpacity
                style={[styles.arrow, styles.leftArrow, isMobile && styles.leftArrowMobile]}
                onPress={handlePrevious}
                accessibilityLabel="previous-image"
              >
                <Icon name="chevron-left" size={30} color="#000" />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.arrow, styles.rightArrow, isMobile && styles.rightArrowMobile]}
                onPress={handleNext}
                accessibilityLabel="next-image"
              >
                <Icon name="chevron-right" size={30} color="#000" />
              </TouchableOpacity>
            </>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -20 }],
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  leftArrow: {
    left: 20,
  },
  rightArrow: {
    right: 20,
  },
  leftArrowMobile: {
    left: -16,
  },
  rightArrowMobile: {
    right: -16,
  },
  imagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
  },
  noImageText: {
    marginTop: 8,
    color: COLORS.mediumGray,
    fontSize: 12,
    fontFamily: 'Satoshi-Regular',
  },
});

export default ImageCarousel;
